import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCasinoConfig,
  useLambdaConfig,
  useOpenaiConfig,
  useUpdateCasinoConfig,
  useUpdateLambdaConfig,
  useUpdateOpenaiConfig,
} from '@/hooks/useApiConfig';

export const Route = createFileRoute('/_authenticated/apis')({
  component: ApisPage,
});

function ApisPage() {
  return (
    <>
      <TopBanner title="APIs" subtitle="Integraciones externas del tenant" />
      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <CasinoCard />
          <OpenAICard />
          <LambdaCard />
        </div>
      </div>
    </>
  );
}

// ---------- Casino ----------
const casinoSchema = z.object({
  url: z.string().url('URL inválida'),
  user: z.string().optional().or(z.literal('')),
  password: z.string().optional().or(z.literal('')),
  token: z.string().min(1, 'Token requerido'),
  cajaId: z.string().optional().or(z.literal('')),
});
type CasinoForm = z.infer<typeof casinoSchema>;

function CasinoCard() {
  const { data, isLoading } = useCasinoConfig();
  const update = useUpdateCasinoConfig();

  const form = useForm<CasinoForm>({
    resolver: zodResolver(casinoSchema),
    defaultValues: { url: 'https://admin.463.life', user: '', password: '', token: '', cajaId: '' },
  });

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Casino (admin.463.life)</CardTitle>
        <CardDescription>Conexión al wallet del operador</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => update.mutate(v))}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL del Admin</FormLabel>
                  <FormControl>
                    <Input placeholder="https://admin.463.life" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Usuario (legacy)</FormLabel>
                  <FormControl>
                    <Input placeholder="Charly365x" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Token *</FormLabel>
                  <FormControl>
                    <Input placeholder="XXXXXXXXX" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cajaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Caja ID</FormLabel>
                  <FormControl>
                    <Input placeholder="1551580" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive-foreground">
              <span className="font-semibold text-destructive">🎰 Salas:</span>{' '}
              <span className="text-muted-foreground">
                No hay salas principales configuradas. Agregalas en la tarjeta <strong>Lambda</strong>.
              </span>
            </div>
          </CardContent>
          <div className="flex justify-end gap-2 p-6 pt-0">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
}

// ---------- OpenAI ----------
const openaiSchema = z.object({
  api_key: z.string().min(1, 'API key requerida'),
  model: z.enum(['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo']),
});
type OpenAIForm = z.infer<typeof openaiSchema>;

function OpenAICard() {
  const { data, isLoading } = useOpenaiConfig();
  const update = useUpdateOpenaiConfig();

  const form = useForm<OpenAIForm>({
    resolver: zodResolver(openaiSchema),
    defaultValues: { api_key: '', model: 'gpt-4o-mini' },
  });

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>OpenAI</CardTitle>
        <CardDescription>OCR de comprobantes (Vision)</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => update.mutate(v))}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="api_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Key *</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="sk-proj-..." disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="gpt-4o-mini">gpt-4o-mini (rapido y barato)</SelectItem>
                      <SelectItem value="gpt-4o">gpt-4o (mas preciso)</SelectItem>
                      <SelectItem value="gpt-4-turbo">gpt-4-turbo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <div className="flex justify-end gap-2 p-6 pt-0">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
}

// ---------- Lambda ----------
const lambdaSchema = z.object({
  enabled: z.boolean().default(false),
  url: z.string().url('URL inválida').or(z.literal('')),
  callback_secret: z.string().optional().or(z.literal('')),
  ids: z.array(z.string()).default([]),
  salas: z
    .array(
      z.object({
        id: z.string().min(1, 'Requerido'),
        name: z.string().min(1, 'Requerido'),
        principal: z.boolean().default(false),
        bonusPercent: z.coerce.number().min(0).max(500).default(0),
      }),
    )
    .default([]),
});
type LambdaForm = z.infer<typeof lambdaSchema>;

function LambdaCard() {
  const { data, isLoading } = useLambdaConfig();
  const update = useUpdateLambdaConfig();

  const form = useForm<LambdaForm>({
    resolver: zodResolver(lambdaSchema),
    defaultValues: {
      enabled: false,
      url: '',
      callback_secret: '',
      ids: [],
      salas: [],
    },
  });

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  const { fields: salaFields, append: appendSala, remove: removeSala } = useFieldArray({
    control: form.control,
    name: 'salas',
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lambda</CardTitle>
        <CardDescription>Integración casino (AWS API Gateway)</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => update.mutate(v))}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Habilitado</FormLabel>
                    <p className="text-xs text-muted-foreground">Activar la integración Lambda</p>
                  </div>
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://xxxxx.execute-api.us-east-2.amazonaws.com/dev/casino/integracion"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="callback_secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Callback Secret</FormLabel>
                  <FormControl>
                    <Input type="password" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <FormLabel>🎰 Salas</FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendSala({ id: '', name: '', principal: false, bonusPercent: 0 })}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Agregar Sala
                </Button>
              </div>
              {salaFields.length === 0 && (
                <p className="text-xs text-muted-foreground">No hay salas configuradas todavía.</p>
              )}
              <div className="space-y-2">
                {salaFields.map((field, idx) => (
                  <div key={field.id} className="grid grid-cols-12 items-end gap-2 rounded-md border p-2">
                    <FormField
                      control={form.control}
                      name={`salas.${idx}.id`}
                      render={({ field: f }) => (
                        <FormItem className="col-span-3">
                          <FormLabel className="text-xs">ID</FormLabel>
                          <FormControl>
                            <Input placeholder="1551580" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`salas.${idx}.name`}
                      render={({ field: f }) => (
                        <FormItem className="col-span-4">
                          <FormLabel className="text-xs">Nombre</FormLabel>
                          <FormControl>
                            <Input placeholder="Principal" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`salas.${idx}.bonusPercent`}
                      render={({ field: f }) => (
                        <FormItem className="col-span-2">
                          <FormLabel className="text-xs">Bono %</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" max="500" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`salas.${idx}.principal`}
                      render={({ field: f }) => (
                        <FormItem className="col-span-2 flex items-center justify-center">
                          <FormControl>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={f.value}
                                onChange={(e) => f.onChange(e.target.checked)}
                              />
                              Principal
                            </label>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="col-span-1 flex justify-end">
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeSala(idx)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          <div className="flex justify-end gap-2 p-6 pt-0">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
}
